import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useStore } from '../store/store'
import { calibrationReport, detectDrift, type Prediction } from '../lib/math'
import { fmtPct, fmtPctValue, fmtSigned } from '../lib/format'
import { Banner, Card, EmptyState, PageHead, Pill, Stat, type Tone } from '../components/ui'
import { CalibrationChart, LineChart } from '../components/charts'

const DRIFT_TONE: Record<string, Tone> = { stable: 'green', drifting: 'amber', broken: 'red', insufficient: 'gray' }

export function Calibration() {
  const predictions = useStore((s) => s.modelPredictions)

  const resolved = useMemo<Prediction[]>(
    () =>
      predictions
        .filter((p) => p.resolved && p.outcome !== undefined)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
        .map((p) => ({ prob: p.estProb, outcome: p.outcome as 0 | 1 })),
    [predictions],
  )

  const report = useMemo(() => calibrationReport(resolved), [resolved])
  const drift = useMemo(() => detectDrift(resolved), [resolved])

  if (resolved.length === 0) {
    return (
      <div className="stack">
        <PageHead title="Calibration & Drift">Are your probabilities honest, and are they still working?</PageHead>
        <EmptyState>
          No resolved predictions yet. Build and resolve predictions in the <Link to="/model">Model Lab</Link> to populate this dashboard.
        </EmptyState>
      </div>
    )
  }

  return (
    <div className="stack">
      <PageHead title="Calibration & Drift">
        A Brier-score dashboard for your probability models. It measures whether your stated probabilities match reality, breaks performance down by confidence bucket, and watches for drift over time.
      </PageHead>

      <div className="grid cols-4">
        <Stat label="Calibration score" value={report.score.toFixed(0)} sub="0–100, higher is better" />
        <Stat label="Brier score" value={report.brier.toFixed(3)} sub="lower is better (0 = perfect)" tone={report.brier < 0.25 ? 'pos' : 'neg'} />
        <Stat label="Expected calibration error" value={fmtPctValue(report.ece * 100, 1)} sub="avg gap, predicted vs actual" />
        <Stat label="Resolved predictions" value={String(report.count)} sub={`bias ${fmtSigned(report.overconfidenceBias * 100, 0)}%`} />
      </div>

      <div className="grid cols-2">
        <Card title="Calibration curve" subtitle="On the diagonal = perfectly calibrated. Below = overconfident.">
          <CalibrationChart buckets={report.buckets} />
          <Banner tone={Math.abs(report.overconfidenceBias) < 0.05 ? 'info' : 'warn'}>{report.verdict}</Banner>
        </Card>

        <Card title="Confidence bucket analysis" subtitle="How often each probability band actually happens">
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Bucket</th><th>Count</th><th>Predicted</th><th>Observed</th><th>Gap</th></tr></thead>
              <tbody>
                {report.buckets.filter((b) => b.count > 0).map((b, i) => (
                  <tr key={i}>
                    <td className="mono">{fmtPct(b.lower, 0)}–{fmtPct(b.upper, 0)}</td>
                    <td className="mono">{b.count}</td>
                    <td className="mono">{fmtPct(b.avgPredicted, 0)}</td>
                    <td className="mono">{fmtPct(b.observedFrequency, 0)}</td>
                    <td className={`mono ${Math.abs(b.gap) > 0.15 ? 'neg' : ''}`}>{fmtSigned(b.gap * 100, 0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="help" style={{ marginTop: 8 }}>A positive gap means outcomes beat your probability (underconfident); negative means you were too sure.</p>
        </Card>
      </div>

      <Card title="Model drift detection" subtitle="Comparing your earlier predictions to your most recent ones">
        <div className="row between" style={{ alignItems: 'center', marginBottom: 8 }}>
          <Pill tone={DRIFT_TONE[drift.verdict]}>{drift.verdict.toUpperCase()}</Pill>
          <span className="muted">PSI {drift.psi.toFixed(2)} ({drift.psiLevel})</span>
        </div>
        <div className="grid cols-4">
          <Stat label="Baseline Brier" value={drift.baselineBrier.toFixed(3)} />
          <Stat label="Recent Brier" value={drift.recentBrier.toFixed(3)} tone={drift.brierDelta > 0.04 ? 'neg' : 'pos'} sub={`${fmtSigned(drift.brierDelta, 3)}`} />
          <Stat label="Baseline accuracy" value={fmtPct(drift.baselineAccuracy, 0)} />
          <Stat label="Recent accuracy" value={fmtPct(drift.recentAccuracy, 0)} tone={drift.accuracyDelta < -0.1 ? 'neg' : 'pos'} sub={`${fmtSigned(drift.accuracyDelta * 100, 0)} pts`} />
        </div>
        {drift.rolling.length > 1 && (
          <div style={{ marginTop: 10 }}>
            <div className="uppercase faint" style={{ marginBottom: 4 }}>Rolling Brier (10-prediction window) — rising = getting worse</div>
            <LineChart series={[{ values: drift.rolling.map((r) => r.brier), color: 'var(--warn)' }]} height={140} yFormat={(n) => n.toFixed(2)} />
          </div>
        )}
        <div className="stack" style={{ marginTop: 10 }}>
          {drift.reasons.map((r, i) => (
            <Banner key={i} tone={drift.verdict === 'broken' ? 'danger' : drift.verdict === 'drifting' ? 'warn' : 'info'}>{r}</Banner>
          ))}
        </div>
      </Card>
    </div>
  )
}
